import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Crown, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { addVipPlanToCart, fetchVipPlans, type VipPlanType } from '../lib/vipApi';

interface VIPSectionCardProps {
  token: string | null;
  isVip: boolean;
  vipExpiry?: string | null;
}

const BENEFITS = [
  'Unlimited daily swipes',
  'Exclusive deals & live drops in your feed',
  'Return credit on eligible VIP plans',
  'Richer mystery mix while you swipe',
];

export default function VIPSectionCard({
  token,
  isVip,
  vipExpiry,
}: VIPSectionCardProps) {
  const [monthlyInr, setMonthlyInr] = useState<string>('299');
  const [yearlyInr, setYearlyInr] = useState<string>('2999');
  const [monthlyPid, setMonthlyPid] = useState<number | null>(null);
  const [yearlyPid, setYearlyPid] = useState<number | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [adding, setAdding] = useState<VipPlanType | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoadingPlans(true);
      const res = await fetchVipPlans(token);
      if (cancelled) return;
      if (res.success && res.data) {
        setMonthlyInr(res.data.monthly.amount_inr);
        setYearlyInr(res.data.yearly.amount_inr);
        if (typeof res.data.monthly.product_id === 'number') {
          setMonthlyPid(res.data.monthly.product_id);
        }
        if (typeof res.data.yearly.product_id === 'number') {
          setYearlyPid(res.data.yearly.product_id);
        }
      }
      setLoadingPlans(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAddToCart = async (plan: VipPlanType) => {
    if (!token) return;
    const pid = plan === 'monthly' ? monthlyPid : yearlyPid;
    if (pid == null) {
      toast.error('Plans still loading — try again in a moment.');
      return;
    }
    setAdding(plan);
    try {
      const res = await addVipPlanToCart(token, pid);
      if (res.success) {
        toast.success('VIP added to cart — open your cart on Discover and checkout to pay and activate.');
      } else {
        toast.error(res.message || 'Could not add VIP to cart');
      }
    } finally {
      setAdding(null);
    }
  };

  if (isVip) {
    return (
      <div className="cosmic-card p-6 space-y-3 border border-amber-500/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Crown size={20} className="text-black" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Jaalu VIP</h2>
            <p className="text-sm text-gray-400">You have active membership.</p>
          </div>
        </div>
        {vipExpiry ? (
          <p className="text-sm text-gray-400">Renews or ends on {new Date(vipExpiry).toLocaleDateString()}</p>
        ) : null}
        <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
          {BENEFITS.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="cosmic-card p-6 space-y-4 border border-border">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center border border-amber-500/30">
          <Crown size={20} className="text-amber-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">Jaalu VIP</h2>
          <p className="text-sm text-gray-400">
            Add to your cart — you pay at checkout; VIP activates after payment with your order.
          </p>
        </div>
      </div>
      <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
        {BENEFITS.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      {loadingPlans ? (
        <p className="text-xs text-gray-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading prices…
        </p>
      ) : null}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          type="button"
          variant="secondary"
          className="flex-1 gap-2 border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
          disabled={!token || adding !== null || monthlyPid == null}
          onClick={() => handleAddToCart('monthly')}
        >
          {adding === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Add monthly — ₹{Number(monthlyInr).toLocaleString('en-IN')}/mo
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          disabled={!token || adding !== null || yearlyPid == null}
          onClick={() => handleAddToCart('yearly')}
        >
          {adding === 'yearly' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Add yearly — ₹{Number(yearlyInr).toLocaleString('en-IN')}
        </Button>
      </div>
    </div>
  );
}
